import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import Logo from "./Logo";
import NotificationBell from "./NotificationBell";
import PushStatusButton from "./PushStatusButton";
import {
  LayoutDashboard,
  ClipboardList,
  AlertTriangle,
  Calendar,
  Users,
  LogOut,
  Menu,
  Sparkles,
  UserCircle,
  Shield,
  Archive,
  Settings,
  MessageSquare,
  GraduationCap,
  ShieldCheck,
  Globe,
  Clock,
  MapPin,
  Star,
  Ship,
  ShoppingCart,
  ShieldAlert,
  Image as ImageIcon,
  CreditCard,
  Receipt,
  Anchor,
} from "lucide-react";
import { useEffect, useState } from "react";
import { ROLES } from "../lib/constants";
import { api } from "../lib/api";

const PUBLIC_SITE = { to: "/hjem", icon: Globe, label: "Hovedsiden", external: true };

const NAV = {
  admin: [
    PUBLIC_SITE,
    { to: "/admin", icon: LayoutDashboard, label: "Oversikt" },
    { to: "/admin/vaskeplan", icon: Sparkles, label: "Vaskeplan", badgeKey: "vaskeplan" },
    { to: "/admin/checklists", icon: ClipboardList, label: "Sjekklister" },
    { to: "/admin/defects", icon: AlertTriangle, label: "Avvik" },
    { to: "/admin/archive", icon: Archive, label: "Arkiv" },
    { to: "/admin/messages", icon: MessageSquare, label: "Meldinger", badgeKey: "messages" },
    { to: "/admin/timesheet", icon: Clock, label: "Timeliste" },
    { to: "/admin/locations", icon: MapPin, label: "Kundeposisjoner" },
    { to: "/admin/surveys", icon: Star, label: "Undersøkelser" },
    { to: "/admin/customer-photos", icon: ImageIcon, label: "Kundebilder", badgeKey: "photos" },
    { to: "/admin/purchases", icon: ShoppingCart, label: "Innkjøpsliste" },
    { to: "/admin/pending-payments", icon: CreditCard, label: "Ventende betalinger", badgeKey: "pending_payments" },
    { to: "/admin/receipts", icon: Receipt, label: "Kvitteringer" },
    { to: "/admin/loft", icon: Anchor, label: "Løft-tjeneste" },
    { to: "/admin/chat-log", icon: MessageSquare, label: "Chat-logg" },
    { to: "/admin/volt-training", icon: GraduationCap, label: "Volt-trening" },
    { to: "/admin/revisjon", icon: ShieldCheck, label: "Revisjonslogg" },
    { to: "/incidents", icon: ShieldAlert, label: "Hendelser" },
    { to: "/admin/bookings", icon: Calendar, label: "Bookinger" },
    { to: "/admin/users", icon: Users, label: "Brukere" },
    { to: "/admin/settings", icon: Settings, label: "Innstillinger" },
  ],
  service: [
    PUBLIC_SITE,
    { to: "/service", icon: LayoutDashboard, label: "Oversikt" },
    { to: "/service/fleet", icon: Ship, label: "Båter" },
    { to: "/service/vaskeplan", icon: Sparkles, label: "Vaskeplan", badgeKey: "vaskeplan" },
    { to: "/service/checklists", icon: ClipboardList, label: "Sjekklister" },
    { to: "/service/defects", icon: AlertTriangle, label: "Avvik" },
    { to: "/service/archive", icon: Archive, label: "Arkiv" },
    { to: "/service/messages", icon: MessageSquare, label: "Meldinger" },
    { to: "/service/timesheet", icon: Clock, label: "Timeliste" },
    { to: "/service/locations", icon: MapPin, label: "Kundeposisjoner" },
    { to: "/service/customer-photos", icon: ImageIcon, label: "Kundebilder", badgeKey: "photos" },
    { to: "/incidents", icon: ShieldAlert, label: "Hendelser" },
  ],
  vask: [
    PUBLIC_SITE,
    { to: "/vask", icon: LayoutDashboard, label: "Oversikt" },
    { to: "/vask/plan", icon: Sparkles, label: "Vaskeplan", badgeKey: "vaskeplan" },
    { to: "/vask/checklists", icon: ClipboardList, label: "Sjekklister" },
    { to: "/vask/defects", icon: AlertTriangle, label: "Avvik" },
    { to: "/vask/messages", icon: MessageSquare, label: "Meldinger" },
    { to: "/vask/timesheet", icon: Clock, label: "Timeliste" },
  ],
};

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [badges, setBadges] = useState({ vaskeplan: 0, photos: 0, pending_payments: 0 });
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/stats/vaskeplan-count");
        if (alive) setBadges((b) => ({ ...b, vaskeplan: data.count || 0 }));
      } catch { /* badge stays stale on transient errors */ }
      // Customer-photo pending review count – only for admin/service.
      if (user && (user.role === "admin" || user.role === "service")) {
        try {
          const r = await api.get("/admin/customer-photos/counts");
          if (alive) setBadges((b) => ({ ...b, photos: r.data?.pending || 0 }));
        } catch { /* keep old count */ }
      }
      // Pending customer payments – admin only.
      if (user && user.role === "admin") {
        try {
          const r = await api.get("/orders");
          const pending = (r.data || []).filter(
            (o) => o.status === "pending" && !o.included_with_booking,
          ).length;
          if (alive) setBadges((b) => ({ ...b, pending_payments: pending }));
        } catch { /* keep old count */ }
      }
    };
    load();
    const id = setInterval(load, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [user]);
  if (!user) return null;
  // Revisjonsloggen vises kun for hovedadmin (SUPER_ADMIN_EMAIL)
  const items = (NAV[user.role] || []).filter(
    (i) => i.to !== "/admin/revisjon" || user.is_super_admin,
  );

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex">
      {/* Sidebar - desktop */}
      <aside className="hidden lg:flex w-64 bg-[#0C1824] text-white flex-col fixed h-screen">
        <div className="px-6 py-6 border-b border-white/10">
          <Logo dark />
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {items.map((it) => it.external ? (
            <a
              key={it.to}
              href={it.to}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`nav-${it.to.replaceAll("/", "-").replace(/^-/, "")}`}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white/80 hover:bg-white/5 hover:text-white transition-colors"
            >
              <it.icon className="w-4 h-4" />
              <span className="flex-1">{it.label}</span>
              <span className="text-[9px] uppercase tracking-widest text-[#BD984D]">↗</span>
            </a>
          ) : (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to.endsWith("admin") || it.to.endsWith("service") || it.to.endsWith("vask")}
              data-testid={`nav-${it.to.replaceAll("/", "-").replace(/^-/, "")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                  isActive
                    ? "bg-[#BD984D] text-[#0C1824] font-semibold"
                    : "text-white/80 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <it.icon className="w-4 h-4" />
              <span className="flex-1">{it.label}</span>
              {it.badgeKey && badges[it.badgeKey] > 0 && (
                <span
                  data-testid={`nav-badge-${it.badgeKey}`}
                  className="inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full bg-[#BD984D] text-[#0C1824] text-[11px] font-bold"
                >
                  {badges[it.badgeKey]}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-white/10">
          <div className="px-3 mb-3">
            <div className="text-xs uppercase tracking-[0.2em] text-[#BD984D]">
              {ROLES[user.role]}
            </div>
            <div className="text-sm font-medium truncate">{user.name}</div>
            <div className="text-xs text-white/60 truncate">{user.email}</div>
          </div>
          <NavLink
            to="/profile"
            data-testid="nav-profile"
            className={({ isActive }) =>
              `w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                isActive ? "bg-white/10 text-white" : "text-white/80 hover:bg-white/5"
              }`
            }
          >
            <UserCircle className="w-4 h-4" /> Min profil
          </NavLink>
          <Link
            to="/personvern"
            target="_blank"
            data-testid="nav-privacy"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white/60 hover:bg-white/5 hover:text-white/80"
          >
            <Shield className="w-4 h-4" /> Personvern
          </Link>
          <button
            data-testid="logout-button"
            onClick={onLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white/80 hover:bg-white/5"
          >
            <LogOut className="w-4 h-4" /> Logg ut
          </button>
        </div>
      </aside>

      {/* Mobile top bar – respects iOS safe-area-inset-top so it clears the notch */}
      <div
        className="lg:hidden fixed top-0 left-0 right-0 bg-[#0C1824] text-white z-40 flex items-center justify-between gap-2 border-b border-white/10"
        style={{
          paddingTop: "calc(env(safe-area-inset-top) + 0.5rem)",
          paddingBottom: "0.5rem",
          paddingLeft: "max(env(safe-area-inset-left), 0.5rem)",
          paddingRight: "max(env(safe-area-inset-right), 0.5rem)",
        }}
      >
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileOpen((v) => !v)}
          className="shrink-0 p-2 rounded-lg hover:bg-white/5 min-h-[44px] min-w-[44px] flex items-center justify-center"
          aria-label="Meny"
        >
          <Menu className="w-6 h-6" />
        </button>
        <div className="shrink-0"><Logo dark size={32} /></div>
        <div className="flex items-center gap-1 shrink-0">
          <PushStatusButton />
          <NotificationBell />
        </div>
      </div>
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-30 bg-black/40"
          onClick={() => setMobileOpen(false)}
        />
      )}
      {mobileOpen && (
        <div
          className="lg:hidden fixed left-0 bottom-0 w-[85vw] max-w-[300px] bg-[#0C1824] text-white z-40 flex flex-col shadow-2xl"
          style={{
            top: "calc(env(safe-area-inset-top) + 60px)",
            paddingBottom: "env(safe-area-inset-bottom)",
          }}
        >
          <nav className="flex-1 overflow-y-auto overscroll-contain px-3 py-4 space-y-1">
            {items.map((it) => it.external ? (
              <a
                key={it.to}
                href={it.to}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm min-h-[44px] text-white/80 hover:bg-white/5 active:bg-white/10"
              >
                <it.icon className="w-4 h-4 shrink-0" />
                <span className="flex-1 truncate">{it.label}</span>
                <span className="text-[9px] uppercase tracking-widest text-[#BD984D] shrink-0">↗</span>
              </a>
            ) : (
              <NavLink
                key={it.to}
                to={it.to}
                onClick={() => setMobileOpen(false)}
                end={it.to.endsWith("admin") || it.to.endsWith("service") || it.to.endsWith("vask")}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-3 rounded-lg text-sm min-h-[44px] ${
                    isActive
                      ? "bg-[#BD984D] text-[#0C1824] font-semibold"
                      : "text-white/80 hover:bg-white/5 active:bg-white/10"
                  }`
                }
              >
                <it.icon className="w-4 h-4 shrink-0" />
                <span className="flex-1 truncate">{it.label}</span>
                {it.badgeKey && badges[it.badgeKey] > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full bg-[#BD984D] text-[#0C1824] text-[11px] font-bold shrink-0">
                    {badges[it.badgeKey]}
                  </span>
                )}
              </NavLink>
            ))}
            <NavLink
              to="/profile"
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-3 rounded-lg text-sm min-h-[44px] mt-3 ${
                  isActive
                    ? "bg-white/10 text-white"
                    : "text-white/80 hover:bg-white/5 active:bg-white/10"
                }`
              }
            >
              <UserCircle className="w-4 h-4 shrink-0" /> Min profil
            </NavLink>
            <Link
              to="/personvern"
              target="_blank"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm text-white/60 hover:bg-white/5 active:bg-white/10 min-h-[44px]"
            >
              <Shield className="w-4 h-4 shrink-0" /> Personvern
            </Link>
          </nav>
          <div className="px-3 py-3 border-t border-white/10 shrink-0">
            <div className="px-3 mb-2 text-sm">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#BD984D]">
                {ROLES[user.role]}
              </div>
              <div className="font-medium truncate">{user.name}</div>
              <div className="text-[11px] text-white/60 truncate">{user.email}</div>
            </div>
            <button
              onClick={onLogout}
              data-testid="mobile-logout-button"
              className="w-full flex items-center gap-2 px-3 py-3 rounded-lg text-sm text-white/80 hover:bg-white/5 active:bg-white/10 min-h-[44px]"
            >
              <LogOut className="w-4 h-4" /> Logg ut
            </button>
          </div>
        </div>
      )}

      {/* Main area */}
      <main className="flex-1 lg:ml-64 pt-[calc(env(safe-area-inset-top)+60px)] lg:pt-0">
        {/* Desktop top bar */}
        <div className="hidden lg:flex sticky top-0 z-30 bg-[#0C1824] text-white items-center justify-between px-8 py-3 border-b border-white/10">
          <div className="text-xs uppercase tracking-[0.32em] text-[#BD984D]">
            Eco-Friendly Boat Rental in Telemark
          </div>
          <div className="flex items-center gap-4">
            <PushStatusButton />
            <NotificationBell />
          </div>
        </div>
        <div className="p-4 lg:p-8 max-w-[1400px]">{children}</div>
      </main>
    </div>
  );
}
